/**
 * Icons.
 *
 * Inline SVG rather than an icon package: the set is small, every icon is a
 * single path, and a dependency that ships 1500 glyphs to render 12 of them is
 * not worth the bytes on a tablet over a mobile connection. Geometry follows
 * Lucide (which is what T3 uses), so the shapes match the reference.
 */

type IconProps = { className?: string };

const base = (className?: string) => ({
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  className,
  "aria-hidden": "true" as const
});

export const IconPlus = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="M5 12h14M12 5v14" />
  </svg>
);

export const IconPen = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="m4 20 4.5-1 9.9-9.9a2.1 2.1 0 0 0-3-3L5.5 16z" />
    <path d="m13.5 7.5 3 3" />
  </svg>
);

export const IconSearch = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

export const IconDevice = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <rect width="20" height="14" x="2" y="3" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </svg>
);

export const IconChevron = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

export const IconChevronRight = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="m9 18 6-6-6-6" />
  </svg>
);

export const IconArrowUp = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="M12 19V5M5 12l7-7 7 7" />
  </svg>
);

export const IconStop = ({ className }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <rect x="7" y="7" width="10" height="10" rx="1.5" />
  </svg>
);

export const IconBack = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="m15 18-6-6 6-6" />
  </svg>
);

export const IconAlert = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="M12 9v4M12 17h.01" />
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
  </svg>
);

export const IconBranch = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="M6 3v12" />
    <circle cx="18" cy="6" r="3" />
    <circle cx="6" cy="18" r="3" />
    <path d="M18 9a9 9 0 0 1-9 9" />
  </svg>
);

export const IconTerminal = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="m4 17 6-6-6-6M12 19h8" />
  </svg>
);

export const IconFolder = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="M3 6.5A1.5 1.5 0 0 1 4.5 5h5l2 2H19.5A1.5 1.5 0 0 1 21 8.5v9A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5z" />
  </svg>
);

export const IconFolderGit = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="M3 6.5A1.5 1.5 0 0 1 4.5 5h5l2 2H19.5A1.5 1.5 0 0 1 21 8.5v9A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5z" />
    <circle cx="16" cy="12" r="1.5" /><circle cx="16" cy="16" r="1.5" /><path d="M16 13.5v1" />
  </svg>
);

export const IconServer = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <rect x="3" y="4" width="18" height="6" rx="1.5" /><rect x="3" y="14" width="18" height="6" rx="1.5" />
    <path d="M7 7h.01M7 17h.01" />
  </svg>
);

/** Zeron's mark: a comet. Drawn rather than imported so the header does not
 * depend on an asset request. */
export const IconComet = ({ className }: IconProps) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <circle cx="14.5" cy="9.5" r="5" fill="currentColor" />
    <path
      d="M11 13 2 22"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      fill="none"
      opacity="0.55"
    />
    <path
      d="M13 15.5 6 22.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      fill="none"
      opacity="0.3"
    />
  </svg>
);
